import { groupBy, map, mean, reduce } from 'lodash';
import { isScorable } from 'js/utils/question-set';

const FULL_CIRCLE = 2 * Math.PI;
const FULL_CIRCLE_EPSILON = 1e-6;

export function isFullCircleTheta(theta) {
  return theta >= FULL_CIRCLE - FULL_CIRCLE_EPSILON;
}

export function listQuestionSets(questionSets) {
  return Object.values(questionSets ?? {});
}

function scorableQuestions(questionSet) {
  return Object.values(questionSet?.questions ?? {}).filter(isScorable);
}

export function radialPieWedges(questionSets) {
  const sets = listQuestionSets(questionSets);

  if (sets.length === 1) {
    const qs = sets[0];
    const questions = scorableQuestions(qs);
    if (questions.length > 0) {
      return {
        flattenToQuestions: true,
        wedges: questions.map((question) => ({
          key: `${qs.key}:${question.key}`,
          category: qs.category,
          normalizedScore: question.normalizedScore,
          payload: qs,
          question,
          kind: 'question',
        })),
      };
    }
  }

  const grouped = groupBy(sets, (qs) => qs.category);
  const wedges = Object.values(grouped).flatMap((categorySets) =>
    categorySets.map((qs) => ({
      key: qs.key,
      category: qs.category,
      normalizedScore: qs.normalizedScore,
      payload: qs,
      kind: 'questionSet',
    }))
  );

  return { flattenToQuestions: false, wedges };
}

export function categoryRingSpans(wedges) {
  const spans = [];
  wedges.forEach((wedge, index) => {
    const last = spans[spans.length - 1];
    if (last && last.category === wedge.category) {
      last.count += 1;
      return;
    }
    spans.push({ category: wedge.category, start: index, count: 1 });
  });
  return spans;
}

export function radialPieSeparators({
  wedges,
  flattenToQuestions,
  rMax,
  rInner,
}) {
  if (wedges.length <= 1) {
    return [];
  }

  if (flattenToQuestions) {
    return wedges.map((_, index) => ({
      index,
      radius: rInner,
      className: 'wedge-divider inner-divider',
    }));
  }

  return wedges.map((wedge, index) => {
    const prev = wedges[index - 1];
    const isCategoryStart = !prev || prev.category !== wedge.category;
    return {
      index,
      radius: isCategoryStart ? rMax : rInner,
      className: isCategoryStart
        ? 'wedge-divider'
        : 'wedge-divider inner-divider',
    };
  });
}

function categoryGroupStats(items, categoryKey, categoryColorMap, details) {
  const { totalScore, count } = reduce(
    items,
    (acc, item) => {
      return item.normalizedScore === null
        ? acc
        : {
            totalScore: acc.totalScore + item.normalizedScore,
            count: acc.count + 1,
          };
    },
    { totalScore: 0, count: 0 }
  );

  const normalizedScore = count > 0 ? totalScore / count : null;
  const variances = map(items, (item) =>
    item.normalizedScore === null || normalizedScore === null
      ? null
      : Math.pow(item.normalizedScore - normalizedScore, 2)
  ).filter((x) => x !== null);

  return {
    key: categoryKey,
    normalizedScore,
    normalizedVariance: variances.length === 0 ? null : mean(variances),
    className: categoryColorMap?.[categoryKey],
    details,
  };
}

export function radialPieOverviewDetailData(questionSets, categoryColorMap) {
  const { flattenToQuestions, wedges } = radialPieWedges(questionSets);

  if (flattenToQuestions) {
    const wedgesByCategory = groupBy(wedges, (wedge) => wedge.category);
    return map(wedgesByCategory, (categoryWedges, categoryKey) =>
      categoryGroupStats(
        categoryWedges,
        categoryKey,
        categoryColorMap,
        categoryWedges.filter((wedge) => wedge.normalizedScore != null)
      )
    );
  }

  const qsByCategory = groupBy(
    listQuestionSets(questionSets),
    (qs) => qs.category
  );
  return map(qsByCategory, (sets, categoryKey) =>
    categoryGroupStats(sets, categoryKey, categoryColorMap, sets)
  );
}
